import { DoorLeftHandClosed } from '~/generated/svg/icon/streamline-ultimate/DoorLeftHandClosed';
import { CalendarDate } from '~/generated/svg/icon/streamline-ultimate/CalendarDate';
import { BuildingNature } from '~/generated/svg/icon/streamline-ultimate/BuildingNature';
import { RealEstateDimensionsBlock } from '~/generated/svg/icon/streamline-ultimate/RealEstateDimensionsBlock';
import { BathroomTubTowel } from '~/generated/svg/icon/streamline-ultimate/BathroomTubTowel';
import { PublicPopoverWidget } from '~/components/widget/PublicPopoverWidget';
import { T } from '@tolgee/react';
import { AppLink } from '~/components/AppLink';
import { Button } from '~/components/ui/Button';
import { EmptyState } from '~/components/empty/EmptyState';

interface Props {
  readonly buildingYear: string;
  readonly renovationYear?: string;
  readonly numberOfRooms: string;
  readonly livingArea: string;
  readonly numberOfBathrooms: string;
  readonly landArea?: string;
  readonly balconyArea?: string;
  readonly link: string;
  readonly isAuthenticated?: boolean;
}

export function PropertyInformationWidget(props: Props) {
  return (
    <PublicPopoverWidget
      isAuthenticated={props.isAuthenticated}
      link={props.link}
      menuItemTranslation="property_information_result_page_login_hint_suffix"
    >
      <WidgetContent {...props} />
    </PublicPopoverWidget>
  );
}

export function WidgetContent(props: Props) {
  const propertyInfoSpecs = [
    { key: 'livingArea', icon: BuildingNature },
    { key: 'buildingYear', icon: CalendarDate },
    {
      key: 'renovationYear',
      icon: CalendarDate,
      labelKey: 'property_form_renovation_year_name',
    },
    { key: 'numberOfRooms', icon: DoorLeftHandClosed },
    { key: 'landArea', icon: RealEstateDimensionsBlock },
    { key: 'numberOfBathrooms', icon: BathroomTubTowel },
    { key: 'balconyArea', icon: RealEstateDimensionsBlock },
  ];

  const visibleSpecs = propertyInfoSpecs.filter((spec) => {
    const value =
      props[spec.key as keyof Omit<Props, 'isAuthenticated' | 'link'>];
    return (
      spec.key === 'renovationYear' ||
      (value !== null && value !== undefined && value !== '')
    );
  });

  return (
    <>
      <div className="h-full flex flex-col gap-y-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
          <T keyName="evaluation_property_information_widget_title" />
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          <T keyName="property_information_widget_text" />
        </p>
        {visibleSpecs.length > 0 ? (
          <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {visibleSpecs.map((spec, idx) => {
              const propertyValue =
                props[
                  spec.key as keyof Omit<Props, 'isAuthenticated' | 'link'>
                ];

              return (
                <PropertyInfo
                  key={idx}
                  icon={spec.icon}
                  value={propertyValue || '–'}
                  labelKey={spec.labelKey ?? `model_property_${spec.key}_name`}
                />
              );
            })}
          </ul>
        ) : (
          <EmptyState
            description="Noch keine Angaben vorhanden."
            compact
          />
        )}
        <AppLink
          to={props.link}
          className="self-start"
          data-track="immodossier valiant property information edit link"
        >
          <Button
            primary
            showArrow
          >
            <T keyName="ui_button_edit" />
          </Button>
        </AppLink>
      </div>
    </>
  );
}

interface PropertyInfoProps {
  readonly icon: React.ComponentType<{ width: number; height: number }>;
  readonly value: string;
  readonly labelKey: string;
}

function PropertyInfo({ icon: Icon, value, labelKey }: PropertyInfoProps) {
  return (
    <li className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 dark:bg-gray-800/60 border border-gray-100 dark:border-gray-700/50">
      {Icon && (
        <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-lg bg-primary-50 dark:bg-primary-900/20 text-primary-500 dark:text-primary-400">
          <Icon
            width={24}
            height={24}
          />
        </div>
      )}
      <div className="flex flex-col min-w-0">
        <span className="text-lg font-bold text-gray-900 dark:text-gray-100 truncate">
          {value}
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          <T keyName={labelKey} />
        </span>
      </div>
    </li>
  );
}
